import React, { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Check, X } from "lucide-react";
import { useFeatures } from "../../context/FeaturesContext";
import { Link } from "react-router-dom";

/**
 * AI Try-on Model panel — feature-gated behind the `ai_try_on` add-on.
 *
 * Workflow:
 *   1. Admin selects one of the existing flat-lay product images as source
 *   2. Picks a background style (studio / lifestyle)
 *   3. Clicks Generate — backend returns 4 angles (front/¾/side/back)
 *   4. Admin ticks which previews to keep
 *   5. Click Save → previews are appended to product.images via `onSave(newUrls)`
 */
export default function TryOnPanel({ productId, sourceImages, onSave }) {
  const { has, loading: featsLoading } = useFeatures();
  const enabled = has("ai_try_on");

  const [sourceUrl, setSourceUrl] = useState(sourceImages?.[0] || "");
  const [style, setStyle] = useState("studio"); // studio | lifestyle
  const [generating, setGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [previews, setPreviews] = useState([]);   // [{angle, url}]
  const [picked, setPicked] = useState({});       // {url: true}
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 4 });

  const togglePick = (url) =>
    setPicked((p) => ({ ...p, [url]: !p[url] }));

  const allPicked = previews.length > 0 && previews.every((p) => picked[p.url]);
  const togglePickAll = () => {
    if (allPicked) {
      setPicked({});
    } else {
      const next = {};
      previews.forEach((p) => { next[p.url] = true; });
      setPicked(next);
    }
  };

  const generate = async () => {
    if (!productId) {
      toast.error("Save the product first, then come back to generate try-on shots.");
      return;
    }
    if (!sourceUrl) {
      toast.error("Pick a source image first");
      return;
    }
    setGenerating(true);
    setPreviews([]);
    setPicked({});
    setFailed([]);
    setProgress({ done: 0, total: 4 });
    try {
      const { data } = await api.post("/admin/try-on/generate", {
        product_id: productId,
        source_image_url: sourceUrl,
        style,
      });
      setJobId(data.job_id);
      setProgress({ done: 0, total: data.total_angles || 4 });
      // Polling loop — kicks off below in the effect that watches jobId
    } catch (e) {
      const detail = e?.response?.data?.detail || "Generation failed";
      toast.error(detail);
      setGenerating(false);
    }
  };

  // Poll the job once we have a job_id. Adds previews to the grid as each
  // angle finishes so the admin sees streaming progress.
  React.useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/admin/try-on/jobs/${jobId}`);
        if (cancelled) return;
        setPreviews(data.previews || []);
        setFailed(data.failed_angles || []);
        const done = (data.previews?.length || 0) + (data.failed_angles?.length || 0);
        setProgress({ done, total: data.total_angles || 4 });
        if (data.status === "done") {
          setGenerating(false);
          if ((data.previews || []).length === 0) {
            toast.error("No images were generated. Try a different source photo.");
          } else {
            toast.success(`Generated ${data.previews.length} angle(s)`);
          }
          return;
        }
        if (data.status === "failed") {
          setGenerating(false);
          toast.error(data.error || "Generation failed");
          return;
        }
        // still running — poll again
        setTimeout(tick, 3000);
      } catch (e) {
        if (cancelled) return;
        setGenerating(false);
        toast.error("Lost connection to the generation job");
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [jobId]);

  const saveSelected = async () => {
    const urls = previews.map((p) => p.url).filter((u) => picked[u]);
    if (urls.length === 0) {
      toast.error("Tick at least one image to keep");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/try-on/save", {
        product_id: productId,
        image_urls: urls,
      });
      toast.success(`Added ${data.added} image(s) to the product gallery`);
      onSave && onSave(data.images || []);
      // Clear the picker so admin sees a fresh slate next time
      setPreviews([]);
      setPicked({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (featsLoading) {
    return <div className="h-32 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />;
  }

  if (!enabled) {
    return (
      <div
        className="border border-dashed border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] p-5 text-center"
        data-testid="try-on-locked"
      >
        <Sparkles size={20} className="mx-auto mb-2 text-[hsl(var(--kb-muted))]" />
        <div className="font-medium mb-1">AI Try-on Model Studio</div>
        <p className="text-sm text-[hsl(var(--kb-muted))] max-w-md mx-auto">
          Auto-generate 4 model angles (front · ¾ · side · back) from any flat-lay product photo.
          Saves hours on catalog photography.
        </p>
        <Link to="/admin/billing" className="kb-btn-primary inline-flex items-center gap-2 text-xs mt-4" data-testid="try-on-upgrade-link">
          Enable in Billing
        </Link>
      </div>
    );
  }

  return (
    <div className="border border-[hsl(var(--kb-border))] bg-white" data-testid="try-on-panel">
      <div className="px-5 py-3 border-b border-[hsl(var(--kb-border))] flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-medium">
          <Sparkles size={16} /> Try on Model — AI Studio
        </div>
        <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))]">Add-on enabled</span>
      </div>

      {!productId && (
        <div className="px-5 py-3 text-xs text-[hsl(var(--kb-muted))] bg-[hsl(var(--kb-surface-alt))] border-b border-[hsl(var(--kb-border))]">
          Save this product once to unlock generation.
        </div>
      )}

      <div className="p-5 space-y-4">
        {/* Source image picker */}
        <div>
          <div className="overline mb-2">Source flat-lay</div>
          {sourceImages?.length ? (
            <div className="flex gap-2 overflow-x-auto pb-2" data-testid="try-on-source-grid">
              {sourceImages.map((src) => {
                const active = src === sourceUrl;
                const display = src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`;
                return (
                  <button
                    key={src}
                    type="button"
                    onClick={() => setSourceUrl(src)}
                    className={`relative w-20 aspect-[3/4] flex-shrink-0 border-2 transition ${active ? "border-[hsl(var(--kb-primary))]" : "border-transparent hover:border-[hsl(var(--kb-border))]"}`}
                    data-testid={`try-on-source-${src.slice(-12)}`}
                    aria-label="Pick as source"
                  >
                    <img src={display} alt="" className="w-full h-full object-cover" />
                    {active && (
                      <span className="absolute top-1 right-1 bg-[hsl(var(--kb-primary))] text-white rounded-full w-4 h-4 flex items-center justify-center">
                        <Check size={10} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-[hsl(var(--kb-muted))]">Upload at least one product image to enable try-on.</p>
          )}
        </div>

        {/* Style picker */}
        <div>
          <div className="overline mb-2">Background style</div>
          <div className="flex gap-2" data-testid="try-on-style-group">
            {[
              { key: "studio", label: "Studio (clean)" },
              { key: "lifestyle", label: "Lifestyle (cultural setting)" },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setStyle(opt.key)}
                className={`px-3 py-2 text-xs border transition ${style === opt.key ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]" : "border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]/50"}`}
                data-testid={`try-on-style-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Generate button */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={!productId || !sourceUrl || generating}
            onClick={generate}
            className="kb-btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="try-on-generate-btn"
          >
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {generating
              ? (progress.done > 0
                  ? `Generating… ${progress.done} of ${progress.total} ready`
                  : "Starting generation…")
              : "Generate 4 angles"}
          </button>
          <span className="text-[11px] text-[hsl(var(--kb-muted))]">
            Takes ~20–40 seconds. Each generation uses your AI add-on quota.
          </span>
        </div>

        {/* Failed angles notice */}
        {failed.length > 0 && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="try-on-failed-banner">
            Some angles couldn't be generated this time ({failed.join(", ")}). You can retry.
          </div>
        )}

        {/* Preview grid */}
        {previews.length > 0 && (
          <div className="border-t border-[hsl(var(--kb-border))] pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="overline">Pick the ones to keep</div>
              <button
                type="button"
                onClick={togglePickAll}
                className="text-xs underline text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))]"
                data-testid="try-on-toggle-all"
              >
                {allPicked ? "Unselect all" : "Select all"}
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="try-on-preview-grid">
              {previews.map((p) => {
                const active = !!picked[p.url];
                const display = p.url.startsWith("http") ? p.url : `${process.env.REACT_APP_BACKEND_URL}${p.url}`;
                return (
                  <button
                    key={p.url}
                    type="button"
                    onClick={() => togglePick(p.url)}
                    className={`relative aspect-[4/5] border-2 group transition ${active ? "border-[hsl(var(--kb-primary))]" : "border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]/50"}`}
                    data-testid={`try-on-preview-${p.angle}`}
                  >
                    <img src={display} alt={p.angle} className="w-full h-full object-cover" />
                    <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] uppercase tracking-wider px-1.5 py-0.5">
                      {p.angle.replace(/_/g, " ")}
                    </span>
                    <span
                      className={`absolute top-1 right-1 rounded-full w-6 h-6 flex items-center justify-center transition ${active ? "bg-[hsl(var(--kb-primary))] text-white" : "bg-white/80 text-[hsl(var(--kb-muted))]"}`}
                      aria-hidden="true"
                    >
                      {active ? <Check size={14} /> : <X size={14} />}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end mt-3">
              <button
                type="button"
                disabled={saving}
                onClick={saveSelected}
                className="kb-btn-primary inline-flex items-center gap-2"
                data-testid="try-on-save-btn"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                {saving ? "Saving…" : "Add selected to product"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
